import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import { ALL_SECTIONS } from '../data/roles';
import { roleService } from '../api/services';

const ROLE_COLORS = ['#C8401E', '#1C5FAD', '#1A7A4A', '#B8720A', '#5B3E9E', '#0E7490', '#92400E'];

export default function RoleEditorModal() {
  const { modals, closeModal, roles, setRoles, showToast } = useApp();
  const open = !!modals['role-editor'];
  const roleId = modals['role-editor-id'];

  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [color, setColor] = useState(ROLE_COLORS[0]);
  const [perms, setPerms] = useState({});
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (roleId) {
      const role = roles.find(r => r.id === roleId);
      if (role) {
        setName(role.name);
        setDesc(role.desc || '');
        setColor(role.color || ROLE_COLORS[0]);
        setPerms(JSON.parse(JSON.stringify(role.perms || {})));
        setIsNew(false);
      }
    } else {
      setName('');
      setDesc('');
      setColor(ROLE_COLORS[0]);
      const blank = ALL_SECTIONS.reduce((o, s) => ({ ...o, [s.key]: { view: false, edit: false } }), {});
      setPerms(blank);
      setIsNew(true);
    }
  }, [open, roleId, roles]);

  function close() { closeModal('role-editor'); }

  function togglePerm(key, type) {
    setPerms(prev => {
      const current = prev[key] || { view: false, edit: false };
      if (type === 'view') {
        const newView = !current.view;
        return { ...prev, [key]: { view: newView, edit: newView ? current.edit : false } };
      }
      if (type === 'edit') {
        const newEdit = !current.edit;
        return { ...prev, [key]: { view: newEdit ? true : current.view, edit: newEdit } };
      }
      return prev;
    });
  }

  function setAll(val) {
    const all = ALL_SECTIONS.reduce((o, s) => ({ ...o, [s.key]: { view: val, edit: val } }), {});
    setPerms(all);
  }

  function save() {
    if (!name.trim()) { alert('Role name is required'); return; }

    if (isNew) {
      const id = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      if (roles.find(r => r.id === id)) { alert('A role with this name already exists'); return; }
      const newRole = { id, name, desc, color, colorLight: color + '22', perms };
      setRoles(prev => [...prev, newRole]);
      roleService.create(newRole).then(created => {
        setRoles(prev => prev.map(r => r.id === id ? created : r));
      }).catch(() => {});
      showToast(`Role "${name}" created`);
    } else {
      const updates = { name, desc, color, perms };
      setRoles(prev => prev.map(r => r.id === roleId ? { ...r, ...updates } : r));
      const role = roles.find(r => r.id === roleId);
      roleService.update(role?._backendId || roleId, updates).catch(() => {});
      showToast(`Role "${name}" updated`);
    }
    close();
  }

  const role = roles.find(r => r.id === roleId);
  const isLocked = role?.locked;

  return (
    <Modal open={open} onClose={close} size="lg" style={{ height: '85vh' }}>
      <ModalHeader
        icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
        title={isNew ? 'New Role' : `Edit Role: ${role?.name}`}
        subtitle="Configure permissions for this role"
        onClose={close}
      />

      <ModalBody>
        {isLocked && (
          <div style={{ background: 'var(--am-l)', border: '1px solid var(--am-b)', borderRadius: 'var(--r)', padding: '8px 12px', marginBottom: 12, fontSize: 11, color: 'var(--am)' }}>
            ⚠️ This is a locked system role. You can view but not edit it.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div className="fg">
            <label className="fl">Role Name *</label>
            <input className="fi" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sales Agent" disabled={isLocked} />
          </div>
          <div className="fg">
            <label className="fl">Description</label>
            <input className="fi" value={desc} onChange={e => setDesc(e.target.value)} placeholder="What this role can do" disabled={isLocked} />
          </div>
        </div>

        <div className="fg" style={{ marginBottom: 14 }}>
          <label className="fl">Role Color</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {ROLE_COLORS.map(c => (
              <div
                key={c}
                onClick={() => !isLocked && setColor(c)}
                style={{
                  width: 24, height: 24, borderRadius: '50%', background: c,
                  cursor: isLocked ? 'default' : 'pointer',
                  border: color === c ? '3px solid var(--tx1)' : '2px solid transparent',
                  boxSizing: 'border-box',
                }}
              />
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx2)' }}>Page Permissions</div>
          {!isLocked && (
            <div style={{ display: 'flex', gap: 5 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAll(true)}>Grant All</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAll(false)}>Revoke All</button>
            </div>
          )}
        </div>

        <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', overflow: 'auto', maxHeight: '40vh' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px', background: 'var(--sur2)', padding: '5px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)' }}>
            <div>Page / Section</div>
            <div style={{ textAlign: 'center' }}>View</div>
            <div style={{ textAlign: 'center' }}>Edit</div>
          </div>
          {ALL_SECTIONS.map((s, i) => {
            const p = perms[s.key] || { view: false, edit: false };
            return (
              <div key={s.key} style={{
                display: 'grid', gridTemplateColumns: '1fr 80px 80px',
                padding: '7px 10px', borderTop: '1px solid var(--bd)',
                background: i % 2 === 0 ? 'var(--sur)' : 'var(--sur2)',
                alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{s.desc}</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox" checked={!!p.view}
                    onChange={() => togglePerm(s.key, 'view')}
                    disabled={isLocked}
                    style={{ cursor: isLocked ? 'default' : 'pointer', width: 15, height: 15 }}
                  />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox" checked={!!p.edit}
                    onChange={() => togglePerm(s.key, 'edit')}
                    disabled={isLocked || !p.view}
                    style={{ cursor: (isLocked || !p.view) ? 'default' : 'pointer', opacity: !p.view ? 0.3 : 1, width: 15, height: 15 }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </ModalBody>

      <ModalFooter>
        <button className="btn btn-ghost" onClick={close}>Cancel</button>
        {!isLocked && (
          <button className="btn btn-primary" onClick={save}>
            {isNew ? 'Create Role' : 'Save Changes'}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}
